import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { i18n } from '@kostbar/i18n'
import type { Action } from '@/shared/constants/actions'
import type {
  RolePermissionsResponse,
  UpdateRolePermissionsRequest,
} from '@/api/types'
import type { RolePermissionsMap } from '@/shared/constants/permissions'
import { getErrorMessage } from '@/shared/utils/error-codes'
import { ApiError } from '@/api/types'
import { rolesApi } from '@/api/endpoints/roles'

interface UseRolePermissionsOptions {
  tenantId?: number | null
  enabled?: boolean
}

export function useRolePermissions(options?: UseRolePermissionsOptions) {
  const query = useQuery({
    queryKey: ['role-permissions', options?.tenantId ?? null],
    queryFn: () => rolesApi.list(options?.tenantId ?? null),
    enabled: options?.enabled ?? true,
  })

  const permissionsByRole = useMemo<RolePermissionsMap | undefined>(() => {
    if (!query.data) return undefined
    const map: RolePermissionsMap = {}
    query.data.roles.forEach((role) => {
      map[role.role] = new Set(role.actions as Array<Action>)
    })
    return map
  }, [query.data])

  return { ...query, permissionsByRole }
}

export function useUpdateRolePermissions(tenantId?: number | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      role,
      data,
    }: {
      role: string
      data: UpdateRolePermissionsRequest
    }) => rolesApi.updatePermissions(role, data, tenantId ?? null),
    onSuccess: () => {
      toast.success(i18n.t('roles:toast.updateSuccess'))
      queryClient.invalidateQueries({ queryKey: ['role-permissions'] })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error(i18n.t('roles:toast.updateFailed'))
      }
    },
  })
}

export type { RolePermissionsResponse }
